import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { useCart } from '../../contexts/CartContext';
import { addressService } from '../../services/addressService';
import { cartService } from '../../services/cartService';
import { gqlRequest } from '../../services/api';
import { DB } from '../../utils/db';
import { BraintreePayment } from './BraintreePayment';

const STEPS = ['shipping', 'payment', 'review'];
const STEP_LABELS = { shipping: 'Shipping', payment: 'Payment', review: 'Review & Place Order' };

function toOrderAddress(addr, user) {
  return {
    firstname: addr.firstName || user?.firstName || '',
    lastname: addr.lastName || user?.lastName || '',
    street: addr.street || '',
    complement: addr.apt || addr.complement || '',
    city: addr.city || '',
    regionCode: addr.state || addr.regionCode || '',
    countryId: addr.country || 'US',
    postcode: addr.zip || addr.postcode || '',
    company: addr.company || '',
    telephone: addr.phone || user?.phone || '',
    email: addr.email || user?.email || ''
  };
}

export function Checkout({ user, onNavigate, onRefresh }) {
  const { brand } = useBrand();
  const toast = useToast();
  const { items, subtotal, itemCount, clearCart } = useCart();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [newAddress, setNewAddress] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    street: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
    phone: user?.phone || '',
    company: ''
  });
  const [useNewAddress, setUseNewAddress] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(true);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState('checkmo');
  const [braintreeNonce, setBraintreeNonce] = useState(null);
  const [braintreeReady, setBraintreeReady] = useState(false);
  const braintreeRef = useRef(null);

  const GATEWAY_CODES = ['braintree'];
  const BRAINTREE_METHODS = ['braintree', 'braintree_ach', 'braintree_paypal'];

  const flattenPaymentMethods = (methods) => {
    const result = [];
    for (const m of methods) {
      if (GATEWAY_CODES.includes(m.code) && m.methods && m.methods.length > 0) {
        for (const sub of m.methods) {
          if (sub.code === 'braintree_cc_vault') continue;
          result.push({
            code: sub.code,
            title: sub.code === 'braintree' ? 'Credit Card' : sub.title,
            gateway: m.code
          });
        }
      } else {
        result.push(m);
      }
    }
    return result;
  };

  const getPaymentIcon = (code) => {
    switch (code) {
      case 'braintree':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><rect x="4" y="4" width="24" height="3" rx="1" fill="#1A56DB"/><rect x="4" y="13" width="8" height="3" rx="1" fill="#D1D5DB"/></svg>;
      case 'braintree_ach':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><path d="M8 6h16M8 10h16M8 14h10" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/></svg>;
      case 'braintree_apple_pay':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><text x="16" y="14" textAnchor="middle" fill="#000" fontSize="10" fontFamily="system-ui" fontWeight="600">Pay</text></svg>;
      case 'braintree_paypal':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><text x="16" y="14" textAnchor="middle" fill="#003087" fontSize="8" fontFamily="system-ui" fontWeight="700">PayPal</text></svg>;
      case 'billcom':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><text x="16" y="14" textAnchor="middle" fill="#00B4E6" fontSize="8" fontFamily="system-ui" fontWeight="700">bill</text></svg>;
      case 'checkmo':
        return <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="0.5" y="0.5" width="31" height="19" rx="3.5" fill="white" stroke="#D1D5DB"/><path d="M8 10l3 3 6-6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
      default:
        return null;
    }
  };

  const [poNumber, setPoNumber] = useState('');
  const [glCode, setGlCode] = useState('');
  const [location, setLocation] = useState('');

  useEffect(() => {
    loadAddresses();
    loadPaymentMethods();
  }, []);

  const loadAddresses = async () => {
    setAddressLoading(true);
    try {
      const result = await addressService.getAddresses({ limit: 20 });
      const addrs = result.addresses || [];
      setAddresses(addrs);
      const defaultShip = addrs.find(a => a.defaultShipping);
      if (defaultShip) {
        setSelectedAddressId(defaultShip.id);
      } else if (addrs.length > 0) {
        setSelectedAddressId(addrs[0].id);
      } else {
        setUseNewAddress(true);
      }
    } catch (err) {
      console.warn('Failed to load addresses, using manual entry:', err.message);
      setUseNewAddress(true);
      if (user?.address) {
        setNewAddress(prev => ({
          ...prev,
          street: user.address.street || '',
          apt: user.address.apt || '',
          city: user.address.city || '',
          state: user.address.state || '',
          zip: user.address.zip || ''
        }));
      }
    } finally {
      setAddressLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    setPaymentLoading(true);
    try {
      const bgMethods = user?.buyerGroup?.paymentMethods;
      if (bgMethods && bgMethods.length > 0) {
        const flat = flattenPaymentMethods(bgMethods);
        setPaymentMethods(flat);
        setSelectedPayment(flat[0].code);
        return;
      }
      try {
        const profileData = await gqlRequest(`
          query { userByToken { buyerGroup { paymentMethods { id code title methods { code title } } } } }
        `, {}, { requireAuth: true });
        const methods = profileData?.userByToken?.buyerGroup?.paymentMethods;
        if (methods && methods.length > 0) {
          const flat = flattenPaymentMethods(methods);
          setPaymentMethods(flat);
          setSelectedPayment(flat[0].code);
          return;
        }
      } catch (e) {
        console.warn('Failed to fetch buyer group payment methods:', e.message);
      }
      const data = await gqlRequest(`
        query PaymentMethods {
          paymentMethods {
            code
            title
          }
        }
      `);
      const methods = data.paymentMethods || [];
      if (methods.length > 0) {
        setPaymentMethods(methods);
        setSelectedPayment(methods[0].code);
      } else {
        setPaymentMethods([
          { code: 'checkmo', title: 'Check / Money Order' },
          { code: 'purchaseorder', title: 'Purchase Order' }
        ]);
      }
    } catch (err) {
      console.warn('Failed to load payment methods, using defaults:', err.message);
      setPaymentMethods([
        { code: 'checkmo', title: 'Check / Money Order' },
        { code: 'purchaseorder', title: 'Purchase Order' }
      ]);
    } finally {
      setPaymentLoading(false);
    }
  };

  const getShippingAddress = () => {
    if (useNewAddress) return newAddress;
    return addresses.find(a => a.id === selectedAddressId) || newAddress;
  };

  const canProceedShipping = () => {
    const addr = getShippingAddress();
    return addr.street && addr.city && addr.state && (addr.zip || addr.postcode);
  };

  const canProceedPayment = () => {
    if (!selectedPayment || selectedPayment.length === 0) return false;
    if (BRAINTREE_METHODS.includes(selectedPayment) && !braintreeNonce) return false;
    return true;
  };

  const handlePlaceOrder = async () => {
    setPlacingOrder(true);
    try {
      const addr = getShippingAddress();
      const shippingAddr = toOrderAddress(addr, user);
      const billingAddr = sameAsBilling ? shippingAddr : shippingAddr;

      const selectedMethod = paymentMethods.find(m => m.code === selectedPayment);

      const manualOrderData = {
        email: user?.email || '',
        items: items.map(item => ({
          sku: item.sku || item.id,
          name: item.name,
          price: item.price,
          total: item.price * item.qty,
          qty: item.qty
        })),
        totals: {
          subtotal,
          discount: 0,
          tax: 0,
          fee: 0,
          shipping: 0,
          grand: subtotal
        },
        paymentInformation: {
          methodTitle: selectedMethod?.title || 'Check / Money Order',
          methodCode: selectedPayment
        }
      };

      if (brand.isB2B) {
        if (poNumber) manualOrderData.poNumber = poNumber;
        if (glCode) manualOrderData.glCode = glCode;
        if (location) manualOrderData.location = location;
      }

      const result = await cartService.createOrder({
        manualOrderData,
        shippingAddress: shippingAddr,
        billingAddress: billingAddr,
        paymentMethod: {
          method: selectedPayment,
          additionalData: braintreeNonce ? { nonce: braintreeNonce } : {}
        },
        userId: user?.id || user?.originUserId
      });

      setOrderResult(result);
      clearCart();
      toast.addToast('Order placed successfully!');
      onRefresh && onRefresh();
    } catch (err) {
      console.warn('API order failed, falling back to localStorage:', err.message);
      try {
        const orderId = `ORD-${Date.now()}`;
        const addr = getShippingAddress();
        const order = {
          id: orderId,
          userId: user?.id,
          items: items.map(item => ({
            sku: item.sku || item.id,
            name: item.name,
            price: item.price,
            qty: item.qty,
            total: item.price * item.qty
          })),
          totals: { subtotal, discount: 0, tax: 0, shipping: 0, grand: subtotal },
          shippingAddress: { street: addr.street, city: addr.city, state: addr.state || addr.regionCode, zip: addr.zip || addr.postcode },
          paymentMethod: selectedPayment,
          poNumber: poNumber || null,
          glCode: glCode || null,
          location: location || null,
          brandKey: brand.key,
          status: 'submitted',
          timeline: [{ status: 'submitted', timestamp: new Date().toISOString() }],
          createdAt: new Date().toISOString()
        };
        const orders = await DB.get('d2p_orders') || [];
        orders.push(order);
        await DB.set('d2p_orders', orders);
        setOrderResult(order);
        clearCart();
        toast.addToast('Order placed (offline mode)');
        onRefresh && onRefresh();
      } catch (fallbackErr) {
        console.error('Fallback order also failed:', fallbackErr);
        toast.addToast('Failed to place order: ' + (err.message || 'Unknown error'), 'error');
      }
    } finally {
      setPlacingOrder(false);
    }
  };

  if (items.length === 0 && !orderResult) {
    return (
      <div style={{
        background: '#F9FAFB',
        minHeight: '100vh',
        padding: '40px 24px',
        fontFamily: 'Libre Franklin, sans-serif'
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', background: '#F3F4F6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '60px auto 20px'
          }}>
            <Icon name="cart" size={40} color="#9CA3AF" />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Your cart is empty</h2>
          <p style={{ color: '#6B7280', marginBottom: 24 }}>Add items before checking out</p>
          <Button icon="arrowLeft" onClick={() => onNavigate(brand.isB2B ? 'catalog' : 'bundle')}>
            {brand.isB2B ? 'Browse Catalog' : 'Order Medications'}
          </Button>
        </div>
      </div>
    );
  }

  if (orderResult) {
    return (
      <div style={{
        background: '#F9FAFB',
        minHeight: '100vh',
        padding: '40px 24px',
        fontFamily: 'Libre Franklin, sans-serif'
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            width: 96, height: 96, borderRadius: '50%', background: '#D1FAE5',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '40px auto 24px'
          }}>
            <Icon name="checkCircle" size={48} color="#10B981" />
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 12, color: brand.c.navy }}>
            Order Placed!
          </h1>
          <p style={{ fontSize: 18, color: '#6B7280', marginBottom: 8 }}>
            Your order has been submitted successfully.
          </p>
          <p style={{ fontSize: 16, fontWeight: 600, color: brand.c.accent, marginBottom: 32 }}>
            Order #{orderResult.id}
          </p>

          <Card style={{ textAlign: 'left', marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: brand.c.navy }}>
              Order Summary
            </h3>
            <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6B7280' }}>Status</span>
                <span style={{ fontWeight: 600 }}>{orderResult.orderStatus || 'OPEN'}</span>
              </div>
              {orderResult.totals && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>Subtotal</span>
                    <span style={{ fontWeight: 600 }}>${(orderResult.totals.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16 }}>
                    <span>Total</span>
                    <span style={{ color: brand.c.accent }}>${(orderResult.totals.grand || 0).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </Card>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Button icon="package" onClick={() => onNavigate(brand.isB2B ? 'purchase-orders' : 'orders')}>
              View Orders
            </Button>
            <Button variant="ghost" onClick={() => onNavigate(brand.isB2B ? 'catalog' : 'dashboard')}>
              {brand.isB2B ? 'Back to Catalog' : 'Back to Dashboard'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentStep = STEPS[step];

  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('cart')}
          style={{ marginBottom: 24 }}
        >
          Back to Cart
        </Button>

        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
          Checkout
        </h1>

        {/* Step Indicators */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 32,
          alignItems: 'center'
        }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div
                onClick={() => i < step && setStep(i)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 16px',
                  borderRadius: 20,
                  background: i === step ? brand.c.accent : i < step ? '#D1FAE5' : '#F3F4F6',
                  color: i === step ? 'white' : i < step ? '#065F46' : '#6B7280',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: i < step ? 'pointer' : 'default',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{
                  width: 24, height: 24, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: i === step ? 'rgba(255,255,255,0.2)' : i < step ? '#10B981' : '#D1D5DB',
                  color: i < step ? 'white' : i === step ? 'white' : '#6B7280',
                  fontSize: 12, fontWeight: 700
                }}>
                  {i < step ? '\u2713' : i + 1}
                </span>
                {STEP_LABELS[s]}
              </div>
              {i < STEPS.length - 1 && (
                <div style={{ width: 32, height: 2, background: i < step ? '#10B981' : '#E5E7EB' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 24,
          alignItems: 'start'
        }}>
          {/* Main Content */}
          <div>
            {/* Shipping Step */}
            {currentStep === 'shipping' && (
              <Card>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                  Shipping Address
                </h2>

                {addressLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: '#6B7280' }}>Loading addresses...</p>
                  </div>
                ) : (
                  <>
                    {addresses.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'grid', gap: 12 }}>
                          {addresses.map(addr => (
                            <label
                              key={addr.id}
                              style={{
                                padding: 16,
                                border: `2px solid ${!useNewAddress && selectedAddressId === addr.id ? brand.c.accent : '#E5E7EB'}`,
                                borderRadius: 12,
                                cursor: 'pointer',
                                display: 'flex',
                                gap: 12,
                                alignItems: 'start',
                                transition: 'all 0.2s'
                              }}
                            >
                              <input
                                type="radio"
                                name="address"
                                checked={!useNewAddress && selectedAddressId === addr.id}
                                onChange={() => {
                                  setUseNewAddress(false);
                                  setSelectedAddressId(addr.id);
                                }}
                                style={{ marginTop: 2 }}
                              />
                              <div>
                                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                                  {addr.firstName} {addr.lastName}
                                  {addr.company && <span style={{ fontWeight: 400, color: '#6B7280' }}> - {addr.company}</span>}
                                </div>
                                <div style={{ fontSize: 14, color: '#6B7280' }}>
                                  {addr.street}{addr.apt ? `, ${addr.apt}` : ''}
                                </div>
                                <div style={{ fontSize: 14, color: '#6B7280' }}>
                                  {addr.city}, {addr.state} {addr.zip}
                                </div>
                                {(addr.defaultShipping || addr.defaultBilling) && (
                                  <div style={{ fontSize: 12, color: brand.c.accent, fontWeight: 600, marginTop: 4 }}>
                                    {addr.defaultShipping && 'Default Shipping'}
                                    {addr.defaultShipping && addr.defaultBilling && ' / '}
                                    {addr.defaultBilling && 'Default Billing'}
                                  </div>
                                )}
                              </div>
                            </label>
                          ))}

                          <label
                            style={{
                              padding: 16,
                              border: `2px solid ${useNewAddress ? brand.c.accent : '#E5E7EB'}`,
                              borderRadius: 12,
                              cursor: 'pointer',
                              display: 'flex',
                              gap: 12,
                              alignItems: 'center',
                              transition: 'all 0.2s'
                            }}
                          >
                            <input
                              type="radio"
                              name="address"
                              checked={useNewAddress}
                              onChange={() => setUseNewAddress(true)}
                            />
                            <Icon name="plus" size={16} color={brand.c.accent} />
                            <span style={{ fontWeight: 600 }}>Add New Address</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {useNewAddress && (
                      <div style={{ display: 'grid', gap: 16 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <InputField label="First Name" value={newAddress.firstName} onChange={v => setNewAddress(p => ({ ...p, firstName: v }))} />
                          <InputField label="Last Name" value={newAddress.lastName} onChange={v => setNewAddress(p => ({ ...p, lastName: v }))} />
                        </div>
                        {brand.isB2B && (
                          <InputField label="Company" value={newAddress.company} onChange={v => setNewAddress(p => ({ ...p, company: v }))} />
                        )}
                        <InputField label="Street Address" value={newAddress.street} onChange={v => setNewAddress(p => ({ ...p, street: v }))} required />
                        <InputField label="Apt / Suite" value={newAddress.apt} onChange={v => setNewAddress(p => ({ ...p, apt: v }))} />
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                          <InputField label="City" value={newAddress.city} onChange={v => setNewAddress(p => ({ ...p, city: v }))} required />
                          <InputField label="State" value={newAddress.state} onChange={v => setNewAddress(p => ({ ...p, state: v }))} required />
                          <InputField label="ZIP Code" value={newAddress.zip} onChange={v => setNewAddress(p => ({ ...p, zip: v }))} required />
                        </div>
                        <InputField label="Phone" value={newAddress.phone} onChange={v => setNewAddress(p => ({ ...p, phone: v }))} />
                      </div>
                    )}

                    <label style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      marginTop: 20,
                      fontSize: 14,
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={sameAsBilling}
                        onChange={e => setSameAsBilling(e.target.checked)}
                      />
                      Billing address same as shipping
                    </label>
                  </>
                )}

                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    icon="arrowRight"
                    onClick={() => setStep(1)}
                    disabled={!canProceedShipping()}
                  >
                    Continue to Payment
                  </Button>
                </div>
              </Card>
            )}

            {/* Payment Step */}
            {currentStep === 'payment' && (
              <Card>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                  Payment Method
                </h2>

                {paymentLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: '#6B7280' }}>Loading payment methods...</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gap: 12 }}>
                      {paymentMethods.map(method => (
                        <label
                          key={method.code}
                          style={{
                            padding: 16,
                            border: `2px solid ${selectedPayment === method.code ? brand.c.accent : '#E5E7EB'}`,
                            borderRadius: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            gap: 12,
                            alignItems: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <input
                            type="radio"
                            name="payment"
                            checked={selectedPayment === method.code}
                            onChange={() => {
                              setSelectedPayment(method.code);
                              setBraintreeNonce(null);
                              setBraintreeReady(false);
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700 }}>{method.title}</div>
                            <div style={{ fontSize: 13, color: '#6B7280' }}>
                              {method.code === 'checkmo' && 'Pay by check or money order'}
                              {method.code === 'purchaseorder' && 'Net 30 payment terms'}
                              {method.code === 'billcom' && 'Bill.com payment processing'}
                            </div>
                          </div>
                          <div style={{ flexShrink: 0 }}>
                            {getPaymentIcon(method.code)}
                          </div>
                        </label>
                      ))}
                    </div>

                    {BRAINTREE_METHODS.includes(selectedPayment) && (
                      <BraintreePayment
                        ref={braintreeRef}
                        selectedMethod={selectedPayment}
                        onNonce={({ nonce }) => setBraintreeNonce(nonce)}
                        onReady={(ready) => setBraintreeReady(ready)}
                        onError={(err) => console.warn('Braintree error:', err)}
                      />
                    )}
                  </>
                )}

                {brand.isB2B && (
                  <div style={{ marginTop: 24 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: brand.c.navy }}>
                      Business Information
                    </h3>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <InputField label="PO Number" value={poNumber} onChange={setPoNumber} placeholder="e.g. PO-2025-001" />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <InputField label="GL Code" value={glCode} onChange={setGlCode} placeholder="e.g. 6100-01" />
                        <InputField label="Location / Facility" value={location} onChange={setLocation} placeholder="e.g. Main Campus" />
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
                  <Button variant="ghost" icon="arrowLeft" onClick={() => setStep(0)}>
                    Back
                  </Button>
                  <Button
                    icon="arrowRight"
                    onClick={() => setStep(2)}
                    disabled={!canProceedPayment()}
                  >
                    Review Order
                  </Button>
                </div>
              </Card>
            )}

            {/* Review Step */}
            {currentStep === 'review' && (
              <div style={{ display: 'grid', gap: 24 }}>
                <Card>
                  <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                    Review Your Order
                  </h2>

                  {/* Shipping Address Review */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8
                    }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: 0 }}>
                        Shipping Address
                      </h3>
                      <button
                        onClick={() => setStep(0)}
                        style={{
                          border: 'none', background: 'none', color: brand.c.accent,
                          fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          fontFamily: 'Libre Franklin, sans-serif'
                        }}
                      >
                        Edit
                      </button>
                    </div>
                    {(() => {
                      const addr = getShippingAddress();
                      return (
                        <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6 }}>
                          <div>{addr.firstName} {addr.lastName}</div>
                          {addr.company && <div>{addr.company}</div>}
                          <div>{addr.street}{addr.apt ? `, ${addr.apt}` : ''}</div>
                          <div>{addr.city}, {addr.state || addr.regionCode} {addr.zip || addr.postcode}</div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Payment Review */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8
                    }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: 0 }}>
                        Payment Method
                      </h3>
                      <button
                        onClick={() => setStep(1)}
                        style={{
                          border: 'none', background: 'none', color: brand.c.accent,
                          fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          fontFamily: 'Libre Franklin, sans-serif'
                        }}
                      >
                        Edit
                      </button>
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280' }}>
                      {paymentMethods.find(m => m.code === selectedPayment)?.title || selectedPayment}
                    </div>
                  </div>

                  {/* B2B Info Review */}
                  {brand.isB2B && (poNumber || glCode || location) && (
                    <div style={{ marginBottom: 20 }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                        Business Information
                      </h3>
                      <div style={{ fontSize: 14, color: '#6B7280' }}>
                        {poNumber && <div>PO Number: {poNumber}</div>}
                        {glCode && <div>GL Code: {glCode}</div>}
                        {location && <div>Location: {location}</div>}
                      </div>
                    </div>
                  )}

                  <div style={{ height: 1, background: '#E5E7EB', margin: '20px 0' }} />

                  {/* Items Review */}
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
                    Items ({itemCount})
                  </h3>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {items.map(item => (
                      <div
                        key={item.sku || item.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          fontSize: 14
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600 }}>{item.name}</span>
                          <span style={{ color: '#6B7280' }}> x {item.qty}</span>
                        </div>
                        <span style={{ fontWeight: 600 }}>${(item.price * item.qty).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button variant="ghost" icon="arrowLeft" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button
                    icon="check"
                    onClick={handlePlaceOrder}
                    disabled={placingOrder}
                  >
                    {placingOrder ? 'Placing Order...' : 'Place Order'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div style={{ position: 'sticky', top: 100 }}>
            <Card>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: brand.c.navy }}>
                Order Summary
              </h3>
              <div style={{ fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280' }}>Items ({itemCount})</span>
                  <span style={{ fontWeight: 600 }}>${subtotal.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280' }}>Shipping</span>
                  <span style={{ fontWeight: 600, color: '#10B981' }}>FREE</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#6B7280' }}>Tax</span>
                  <span style={{ fontWeight: 600 }}>$0.00</span>
                </div>
                <div style={{ height: 1, background: '#E5E7EB', margin: '12px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700 }}>
                  <span>Total</span>
                  <span style={{ color: brand.c.accent }}>${subtotal.toFixed(2)}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, required, placeholder, type = 'text' }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '10px 12px',
          border: '2px solid #E5E7EB',
          borderRadius: 8,
          fontSize: 14,
          fontFamily: 'Libre Franklin, sans-serif',
          boxSizing: 'border-box'
        }}
      />
    </div>
  );
}
